import { Decimal, type D, ZERO } from './decimal'
import { GLOBAL } from '../config/global'
import { LETTERS_BY_ID, LETTER_ID_BY_CHAR, type LetterDef } from '../config/letters'
import { serifMultiplier } from './operators'

export interface LetterState {
  id: number
  level: number // 0 = not owned
  capital: boolean
}

// Letters whose presence in a word boosts the burst's rare multiplier (§4).
export const RARE_BURST_LETTERS = new Set(['Q', 'Z', 'J', 'X', 'V', 'K'])

// Cost to buy the next level (level -> level+1). Buying level 1 costs unlockCost.
// Soft cap (§8.1): levels at/below softCapLevel scale by 1.12; beyond it the
// per-level multiplier steepens to softCapCostMultiplier.
export function nextLevelCost(def: LetterDef, currentLevel: number): D {
  const cap = GLOBAL.softCapLevel
  const base = Math.min(currentLevel, cap)
  const over = Math.max(0, currentLevel - cap)
  return new Decimal(def.unlockCost)
    .mul(Decimal.pow(GLOBAL.levelCostMultiplier, base))
    .mul(Decimal.pow(GLOBAL.softCapCostMultiplier, over))
}

// Per-letter passive output (Ink/s). Gated below passiveUnlockLevel (onboarding).
export function letterOutput(state: LetterState): D {
  if (state.level < GLOBAL.passiveUnlockLevel) return ZERO
  const def = LETTERS_BY_ID[state.id]
  let out = new Decimal(def.baseOutput).mul(
    Decimal.pow(GLOBAL.levelOutputMultiplier, state.level - 1),
  )
  if (state.capital) out = out.mul(GLOBAL.capOutputMultiplier)
  return out
}

export function totalOutput(
  letters: LetterState[],
  heat = 0,
  activeIds?: number[],
  typeface?: Record<string, number>,
): D {
  const filtered = activeIds && activeIds.length > 0
    ? letters.filter((s) => activeIds.includes(s.id))
    : letters
  const raw = filtered.reduce((acc, s) => acc.add(letterOutput(s)), ZERO)
  const withForge = raw.mul(forgeMultiplier(heat))
  return typeface ? withForge.mul(serifMultiplier(typeface)) : withForge
}

// Manual tap value: a slice of total output, with a floor for early game.
export function tapValue(
  letters: LetterState[],
  weight = 0,
  activeIds?: number[],
  typeface?: Record<string, number>,
): D {
  const base = totalOutput(letters, 0, activeIds, typeface).mul(GLOBAL.tapFractionOfOutput)
  const floor = new Decimal(GLOBAL.tapFloor)
  const raw = base.lt(floor) ? floor : base
  return raw.mul(hammerMultiplier(weight))
}

export function distinctOwned(letters: LetterState[]): number {
  return letters.filter((l) => l.level > 0).length
}

// --- Ink / Press metrics (Milestone 2.5) --------------------------------

// Ink freshness multiplier: 1 + ink/200. ink=100 → 1.5x, ink=-100 → 0.5x.
export function forgeMultiplier(heat: number): number {
  return 1 + heat / GLOBAL.forgeMultiplierScale
}

// Press multiplier: same curve applied to roller pressure.
export function hammerMultiplier(weight: number): number {
  return 1 + weight / GLOBAL.hammerMultiplierScale
}

// Length delta from a crafted word: pivot at 5, >5 adds, <5 subtracts.
export function lengthDelta(wordLength: number): number {
  return wordLength - GLOBAL.lengthPivot
}

// --- Wordsmith (Milestone 2) -------------------------------------------------

// A letter is "owned" (and thus "opened", per the M2.5 decision) at level > 0.
export function ownedLetterChars(letters: LetterState[]): Set<string> {
  const owned = new Set<string>()
  for (const l of letters) {
    if (l.level > 0) owned.add(LETTERS_BY_ID[l.id].letter)
  }
  return owned
}

// A word is craftable only if every one of its letters is owned.
export function canCraftWord(word: string, owned: Set<string>): boolean {
  const w = word.toUpperCase()
  for (const ch of w) {
    if (!(ch in LETTER_ID_BY_CHAR)) return false
    if (!owned.has(ch)) return false
  }
  return w.length > 0
}

export function countRareLetters(word: string): number {
  let n = 0
  for (const ch of word.toUpperCase()) if (RARE_BURST_LETTERS.has(ch)) n++
  return n
}

// Per-word reward multiplier from its level (§4.2): +4% per level above Lv1.
export function wordLevelMultiplier(level: number): number {
  return 1 + GLOBAL.wordLevelMultPerLevel * Math.max(0, level - 1)
}

// Combo chain multiplier (§8.6): +5% per link beyond the first, capped at +50%.
export function comboMultiplier(chainLength: number): number {
  const bonus = Math.min(
    GLOBAL.comboBonusPerLink * Math.max(0, chainLength - 1),
    GLOBAL.comboBonusCap,
  )
  return 1 + bonus
}

// XP (uses) required to advance from `level` to `level + 1`.
export function wordXpToNext(level: number): number {
  return level * GLOBAL.wordXpPerLevel
}

export interface BurstContext {
  word: string
  outputPerSec: D
  wordLevel: number
  chainLength: number
  isDaily: boolean
  globalMultiplier: number // combined Set + Mastery bonus, pre-cap
}

// Word burst (§4): (Ink/s) × (scalar × len^exp) × (1 + rareBonus × rareCount)
// × wordLevel × combo × daily, then × the capped global multiplier (§8.8).
export function wordBurst(ctx: BurstContext): D {
  const len = ctx.word.length
  const lengthFactor = GLOBAL.burstLengthScalar * Math.pow(len, GLOBAL.burstLengthExponent)
  const rareFactor = 1 + GLOBAL.burstRareBonus * countRareLetters(ctx.word)
  const daily = ctx.isDaily ? 1 + GLOBAL.dailyWordBonus : 1
  const capped = Math.min(ctx.globalMultiplier, GLOBAL.globalMultiplierHardCap)
  const mult =
    lengthFactor *
    rareFactor *
    wordLevelMultiplier(ctx.wordLevel) *
    comboMultiplier(ctx.chainLength) *
    daily *
    capped
  return ctx.outputPerSec.mul(mult)
}
